
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel, summarizeGameState } from "@/utils/debugUtils";
import { WordPair } from "@/types/game";

export const useGameStateMonitoring = (
  gameId: string | null,
  rounds: WordPair[],
  botIsThinking: boolean, 
  botHasChosenWord: boolean,
  userHasSubmitted: boolean,
  processingComplete: boolean,
  isSubmitting: boolean
) => {
  // CRITICAL FIX: Add effect to monitor state and log it for debugging/monitoring
  useEffect(() => {
    debugLog(DebugLevel.INFO, "useGameLogic", "Critical state changes detected", { 
      botIsThinking, 
      botHasChosenWord,
      userHasSubmitted,
      processingComplete,
      isSubmitting,
      roundsLength: rounds.length
    });
    
    // Check if we're in the state where both user has submitted and bot has chosen,
    // but processing hasn't completed - this might indicate a stuck state
    if (userHasSubmitted && botHasChosenWord && !processingComplete && !isSubmitting) {
      debugLog(DebugLevel.WARN, "useGameLogic", "Detected potential stuck state - user submitted, bot has word, but processing incomplete");
    }
  }, [botIsThinking, botHasChosenWord, userHasSubmitted, processingComplete, isSubmitting, rounds.length]);

  // Log state changes for debugging
  useEffect(() => {
    debugLog(DebugLevel.INFO, "useGameLogic", "Game state updated", { 
      botIsThinking, 
      userHasSubmitted,
      processingComplete,
      roundsLength: rounds.length
    });
    
    // Every time the state changes, check for inconsistencies
    const checkGameState = async () => {
      if (gameId && rounds.length > 0) {
        await summarizeGameState(
          supabase,
          gameId,
          rounds.length,
          botIsThinking,
          botHasChosenWord,
          userHasSubmitted,
          processingComplete
        );
      }
    };
    
    checkGameState();
  }, [gameId, botIsThinking, userHasSubmitted, processingComplete, rounds.length, botHasChosenWord]);
};
